import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { MaskText, FadeUp } from "@/components/motion/Reveal";
import { createLead } from "@/lib/api";
import SmsConsent from "@/components/SmsConsent";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const mascotY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const cloudY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const archScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  const [form, setForm] = useState({ name: "", phone: "", preferred_time: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const requestCallback = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await createLead({ ...form, source: "callback" });
      setDone(true);
      toast.success("Got it! A Advanced Life Policies advisor will call you back shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen bg-forest text-cream grain overflow-hidden pt-28 pb-20 lg:pt-32"
      data-testid="hero-section"
    >
      {/* sky backdrop */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1616783749802-6c17126d80e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBnbGFzcyUyMGFyY2hpdGVjdHVyZSUyMHNreSUyMGNsb3Vkc3xlbnwwfHx8fDE3ODgzNjIxMDd8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          aria-hidden
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-forest/85" />
      </div>

      {/* subtle cool gradient wash */}
      <div className="pointer-events-none absolute inset-0 opacity-70 bg-[radial-gradient(circle_at_18%_15%,rgba(2,132,199,0.25)_0%,transparent_55%),radial-gradient(circle_at_82%_85%,rgba(125,211,252,0.15)_0%,transparent_50%)]" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10">
        {/* Masthead row — magazine-style top rule */}
        <FadeUp delay={0.02}>
          <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.28em] text-cream/50 pb-6 border-b border-cream/15">
            <span data-testid="hero-badge">Vol. 01 — Insurance · Tax · Planning</span>
            <span className="hidden sm:inline">Est. 2026 — New York</span>
          </div>
        </FadeUp>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 pt-14 lg:pt-20 items-start">
          {/* Left: editorial title */}
          <div className="lg:col-span-7">
            <FadeUp delay={0.15}>
              <p className="text-sm text-cream/60 flex items-center gap-3">
                <span className="w-8 h-px bg-cream/40" />
                A quieter kind of firm.
              </p>
            </FadeUp>
            <h1 className="mt-6 font-serif font-light text-cream text-6xl sm:text-7xl lg:text-[92px] leading-[0.95] tracking-tight">
              <MaskText
                lines={[
                  "Money,",
                  <>looked after<span className="italic text-sage-light">.</span></>,
                  <span className="italic text-cream/60">Properly.</span>,
                ]}
              />
            </h1>

            <FadeUp delay={0.7} className="mt-8 max-w-lg">
              <p className="text-base lg:text-lg leading-relaxed text-cream/70">
                Refinance your life insurance, pay less tax, and get a plan for the rest of it — with a
                licensed advisor who actually calls you back. One firm, three things you shouldn&apos;t have to think about.
              </p>
            </FadeUp>

            <FadeUp delay={0.85} className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              <div>
                <p className="font-serif text-4xl text-cream">22%</p>
                <p className="text-[11px] uppercase tracking-[0.16em] text-cream/50 mt-1">Avg. saved on cover</p>
              </div>
              <div>
                <p className="font-serif text-4xl text-cream">24h</p>
                <p className="text-[11px] uppercase tracking-[0.16em] text-cream/50 mt-1">To an advisor</p>
              </div>
              <div>
                <p className="font-serif text-4xl text-cream">1:1</p>
                <p className="text-[11px] uppercase tracking-[0.16em] text-cream/50 mt-1">Never a call centre</p>
              </div>
            </FadeUp>
          </div>

          {/* Right: callback form on cream card */}
          <div className="lg:col-span-5 lg:pt-4">
            <FadeUp delay={0.4}>
              {!done ? (
                <form
                  onSubmit={requestCallback}
                  className="rounded-2xl bg-cream text-forest border border-cream/20 shadow-2xl shadow-black/40 p-7 sm:p-8 space-y-4"
                  data-testid="callback-form"
                >
                  <div className="flex items-center gap-3 pb-2 border-b border-forest/10">
                    <span className="grid place-items-center h-10 w-10 rounded-full bg-forest text-cream">
                      <Phone className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="font-serif text-xl leading-none">Request a callback</p>
                      <p className="text-xs text-forest/55 mt-1.5">A licensed advisor. Zero pressure.</p>
                    </div>
                  </div>

                  <Input placeholder="Your name" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="h-12 rounded-lg bg-white border-forest/15 text-forest placeholder:text-forest/40"
                    data-testid="callback-name" />
                  <Input type="tel" placeholder="Phone number" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="h-12 rounded-lg bg-white border-forest/15 text-forest placeholder:text-forest/40"
                    data-testid="callback-phone" />
                  <Select value={form.preferred_time} onValueChange={(v) => setForm({ ...form, preferred_time: v })}>
                    <SelectTrigger className="h-12 rounded-lg bg-white border-forest/15 text-forest" data-testid="callback-time">
                      <SelectValue placeholder="Best time to call (optional)" />
                    </SelectTrigger>
                    <SelectContent data-testid="callback-time-options">
                      {["Morning (8am–12pm)", "Afternoon (12pm–5pm)", "Evening (5pm–8pm)", "Anytime"].map((t) => (
                        <SelectItem key={t} value={t} data-testid={`callback-time-${t}`}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Button type="submit" disabled={submitting} data-testid="callback-submit"
                    className="w-full h-12 rounded-full bg-terracotta hover:bg-terracotta-dark text-cream text-base font-semibold shadow-lg transition-all duration-300 hover:-translate-y-0.5 active:scale-95">
                    {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Request a callback"}
                  </Button>
                  <p className="text-center text-xs text-forest/55">
                    Prefer to talk now?{" "}
                    <a href="tel:+19179053568" data-testid="hero-phone"
                      className="font-semibold text-forest underline underline-offset-2 hover:text-terracotta transition-colors">
                      (917) 905-3568
                    </a>
                  </p>
                  <SmsConsent className="text-center pt-1" />
                </form>
              ) : (
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                  className="rounded-2xl bg-cream border border-sage/40 shadow-2xl p-8 text-center"
                  data-testid="callback-success">
                  <CheckCircle2 className="h-10 w-10 text-sage mx-auto" />
                  <p className="mt-3 font-serif text-2xl text-forest">You&apos;re all set, {form.name.split(" ")[0]}!</p>
                  <p className="mt-2 text-sm text-forest/65">
                    A licensed Advanced Life Policies advisor will call {form.phone} shortly.
                  </p>
                </motion.div>
              )}
            </FadeUp>
          </div>
        </div>
      </div>

      <motion.button
        onClick={() => scrollTo("marquee")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-2 text-cream/40 hover:text-cream transition-colors"
        data-testid="hero-scroll-cue"
      >
        <span className="text-[11px] uppercase tracking-[0.25em]">Continue</span>
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.button>
    </section>
  );
}
